import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

function labelNode(title, detail) {
  const node = document.createElement('div');
  const strong = document.createElement('strong');
  const small = document.createElement('small');
  strong.textContent = title;
  small.textContent = detail;
  node.append(strong, small);
  return node;
}

export default function CoverageMap({ zones = [], leads = [], onLeadSelect, onAreaSelect, compact = false, loading = false }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const selectRef = useRef(onLeadSelect);
  const areaSelectRef = useRef(onAreaSelect);
  selectRef.current = onLeadSelect;
  areaSelectRef.current = onAreaSelect;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true, minZoom: 3 }).setView([-15.4, -51.5], 4);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    map.on('click', (event) => areaSelectRef.current?.({ lat: event.latlng.lat, lng: event.latlng.lng }));
    const observer = new ResizeObserver(() => map.invalidateSize({ animate: false }));
    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const bounds = L.latLngBounds([]);

    const zoneColors = ['#565B63', '#C27A40', '#3F806D', '#8B6072', '#9A803F', '#6E665D'];
    zones.forEach((zone, index) => {
      if (!Number.isFinite(Number(zone.lat)) || !Number.isFinite(Number(zone.lng))) return;
      const color = zoneColors[index % zoneColors.length];
      const circle = L.circle([zone.lat, zone.lng], {
        radius: Number(zone.radius || 8000), color, weight: zone.manual ? 2.5 : 1.5, opacity: .86, fillColor: color, fillOpacity: zone.manual ? .16 : .1,
      }).addTo(layer);
      L.circleMarker([zone.lat, zone.lng], { radius: zone.manual ? 8 : 5, color: '#ffffff', weight: 2, fillColor: color, fillOpacity: 1 })
        .bindTooltip(labelNode(zone.name, `${Math.round((zone.radius || 8000) / 1000)} km de cobertura`), { direction: 'top', className: 'nextfy-map-tooltip' })
        .addTo(layer);
      bounds.extend(circle.getBounds());
    });

    leads.forEach((lead) => {
      if (!Number.isFinite(Number(lead.latitude)) || !Number.isFinite(Number(lead.longitude))) return;
      const markerColor = lead.score >= 85 ? '#168C72' : lead.score >= 70 ? '#9A772D' : '#555A63';
      const marker = L.circleMarker([lead.latitude, lead.longitude], {
        radius: lead.score >= 85 ? 7 : 5, color: '#ffffff', weight: 2, fillColor: markerColor, fillOpacity: 1,
      }).bindTooltip(labelNode(lead.name, `${lead.score} pontos · ${lead.city}`), { direction: 'top', className: 'nextfy-map-tooltip' }).addTo(layer);
      if (selectRef.current) {
        marker.on('click', () => selectRef.current(lead));
        marker.getElement()?.setAttribute('aria-label', `Abrir ${lead.name}, ${lead.score} pontos`);
      }
      bounds.extend([lead.latitude, lead.longitude]);
    });

    if (bounds.isValid()) map.fitBounds(bounds, { padding: compact ? [18, 18] : [34, 34], maxZoom: 13, animate: false });
    else map.setView([-15.4, -51.5], 4, { animate: false });
  }, [zones, leads, compact]);

  return <div className={`coverage-map ${compact ? 'coverage-map--compact' : ''} ${onAreaSelect ? 'coverage-map--selectable' : ''}`}>
    <div ref={containerRef} className="coverage-map__canvas" role="region" aria-label="Mapa das cidades, zonas de busca e empresas encontradas" />
    {onAreaSelect && !loading && <div className="coverage-map__hint"><span>+</span><strong>Clique no mapa para posicionar a busca</strong></div>}
    {loading && <div className="coverage-map__loading"><span /><strong>Localizando cidades…</strong></div>}
    {!loading && !zones.length && <div className="coverage-map__empty"><strong>Adicione uma cidade</strong><span>A área de cobertura aparecerá aqui.</span></div>}
    {!!zones.length && <div className="coverage-map__legend"><span><i className="legend-zone" /> área de busca</span>{!!leads.length && <span><i className="legend-lead" /> lead encontrado</span>}</div>}
  </div>;
}
